// GLSL for the projection engine. Written as GLSL 1 style; three.js converts it for WebGL 2.
// String.raw keeps the line-continuation backslashes in the #define macros.

/** World-space position and normal, shared by every surface material. */
export const VS_WORLD = String.raw`
varying vec3 vW;
varying vec3 vN;
void main(){
  vec4 w = modelMatrix * vec4(position, 1.0);
  vW = w.xyz;
  vN = normalize(mat3(modelMatrix) * normal);
  gl_Position = projectionMatrix * viewMatrix * w;
}`;

/** Full-screen quad for post passes. */
export const VS_QUAD = String.raw`
varying vec2 vUv;
void main(){ vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }`;

/** Equirectangular lookup shared by the projection shaders. Heading 0 faces -Z. */
const EQ = String.raw`
const float PI = 3.141592653589793;
vec2 eq(vec3 d, float yaw){
  float lon = atan(d.x, -d.z) - yaw;
  float u = fract(lon / (2.0 * PI) + 0.5);
  if (flipH > 0.5) u = 1.0 - u;
  float v = asin(clamp(d.y, -1.0, 1.0)) / PI + 0.5;
  return vec2(u, v);
}
/* Samples a panorama. Behind the camera the image's left and right edges meet and u jumps from 1 to 0;
   the graphics card would read that jump as "very far away" and use its blurriest copy of the image there,
   which shows as a thin dashed line. Taking the rate of change from a u that wraps on the other side fixes it. */
vec4 eqT(sampler2D T, vec2 uv){
#ifdef texture2DGradEXT
  vec2 gx = dFdx(uv), gy = dFdy(uv);
  float u2 = fract(uv.x + 0.5);
  float gx2 = dFdx(u2), gy2 = dFdy(u2);
  if (abs(gx2) < abs(gx.x)) gx.x = gx2;
  if (abs(gy2) < abs(gy.x)) gy.x = gy2;
  return texture2DGradEXT(T, uv, gx, gy);
#else
  return texture2D(T, uv);
#endif
}`;

const SLOT_UNIFORMS = String.raw`
uniform sampler2D tex0; uniform sampler2D tex1; uniform sampler2D tex2; uniform sampler2D tex3;
uniform samplerCube dep0; uniform samplerCube dep1; uniform samplerCube dep2; uniform samplerCube dep3;
uniform vec3 pPos[4];
uniform float pYaw[4];
uniform vec3 pCol[4];
uniform float flipH;
uniform float dbg;`;

/**
 * Walk and hop view: the nearest four renders, blended by camera distance (pW, set on the CPU),
 * by how closely each render's line of sight matches the viewer's (per pixel), and by visibility
 * from each render's depth map, or the proxy's depth cube when it has none (occlusion).
 */
export const FS_PROJECT = String.raw`
precision highp float;
${SLOT_UNIFORMS}
uniform float pW[4];
uniform vec3 camPos;
uniform float perPixel;
uniform float useOcc;
uniform sampler2D cmpTex;
uniform float cmpSlot;
uniform float cmpX;
uniform sampler2D eqd0; uniform sampler2D eqd1; uniform sampler2D eqd2; uniform sampler2D eqd3;
uniform float pEq[4];
/* slot of the render whose depth mesh is being drawn (-1 for the proxy) */
uniform float own;
varying vec3 vW;
varying vec3 vN;
${EQ}
#define SLOT(I, T, D, E) if (pW[I] > 0.0) { \
  vec3 dv = vW - pPos[I]; \
  float dist = max(length(dv), 1e-4); \
  vec3 dn = dv / dist; \
  vec2 suv = eq(dn, pYaw[I]); \
  /* a depth mesh's edge reaches a little past the object (vertices are a few pixels apart): */ \
  /* trim it where its own render saw something further away at this exact pixel */ \
  if (own == float(I) && pEq[I] > 0.5 && dist < texture2D(E, suv).r - 0.04 - dist * 0.02) discard; \
  vec3 c = (cmpSlot == float(I) && gl_FragCoord.x > cmpX) ? eqT(cmpTex, suv).rgb : eqT(T, suv).rgb; \
  if (dbg > 0.5) c = mix(c, pCol[I], 0.55); \
  float vis = 1.0; \
  if (useOcc > 0.5) { \
    /* the render's own depth map when it has one (exact), else the proxy's depth cube */ \
    float sd = pEq[I] > 0.5 ? texture2D(E, suv).r : textureCube(D, dn).r; \
    /* a render's own mesh only follows its depth cell by cell, so it gets more slack */ \
    if (own == float(I)) vis = 1.0 - smoothstep(sd * 1.12 + 0.05, sd * 1.2 + 0.1, dist); \
    else vis = 1.0 - smoothstep(sd + 0.03 + dist * 0.012, sd + 0.10 + dist * 0.03, dist); \
    /* standing on (or right by) a render, its picture is exact along every view ray, whatever the proxy's */ \
    /* shape: never hand its pixels to the other renders there (they tear things the proxy lacks, like chairs) */ \
    if (own < -0.5) vis = max(vis, 1.0 - smoothstep(0.02, 0.3, length(camPos - pPos[I]))); \
  } \
  float ang = acos(clamp(dot(vc, dn), -1.0, 1.0)); \
  float aw = mix(1.0, 1.0 / (ang + 0.05), perPixel); \
  float w = pW[I] * aw; \
  acc += c * w * vis; tw += w * vis; \
  facc += c * w; ftw += w; \
}
void main(){
  vec3 vc = normalize(vW - camPos);
  vec3 acc = vec3(0.0); float tw = 0.0;
  vec3 facc = vec3(0.0); float ftw = 0.0;
  SLOT(0, tex0, dep0, eqd0)
  SLOT(1, tex1, dep1, eqd1)
  SLOT(2, tex2, dep2, eqd2)
  SLOT(3, tex3, dep3, eqd3)
  vec3 col = tw > 1e-5 ? acc / tw : (ftw > 1e-6 ? facc / ftw : vec3(0.3));
  if (tw <= 1e-5 && pEq[0] > 0.5 && pW[0] > 0.0) {
    /* nothing nearby saw this spot (it was behind a lamp, say): borrow the nearest render's colour from */
    /* just beside the thing in the way, where it did see this far, rather than painting the lamp here */
    vec3 dv = vW - pPos[0];
    float dist = length(dv);
    vec2 uv0 = eq(dv / max(dist, 1e-4), pYaw[0]);
    vec3 fc = vec3(0.0); float fw = 0.0;
    for (int k = 0; k < 5; k++) {
      float r = 0.0012 * pow(2.0, float(k));
      for (int a = 0; a < 8; a++) {
        float t = float(a) * 0.785398;
        vec2 uv = uv0 + vec2(cos(t) * r * 0.5, sin(t) * r);
        if (texture2D(eqd0, uv).r > dist - 0.05 - dist * 0.03) { fc += texture2D(tex0, uv).rgb; fw += 1.0; }
      }
      if (fw > 0.0) break;
    }
    if (fw > 0.0) col = fc / fw;
  }
  gl_FragColor = vec4(col, 1.0);
}`;

/** Distance from a pano centre, written into its depth cube. */
export const FS_DISTANCE = String.raw`
precision highp float;
uniform vec3 center;
varying vec3 vW;
varying vec3 vN;
void main(){ gl_FragColor = vec4(length(vW - center), 0.0, 0.0, 1.0); }`;

/** Cube map to equirectangular. */
export const FS_CUBE_TO_EQUIRECT = String.raw`
precision highp float;
uniform samplerCube cube;
varying vec2 vUv;
const float PI = 3.141592653589793;
void main(){
  float lon = (vUv.x - 0.5) * 2.0 * PI;
  float lat = (vUv.y - 0.5) * PI;
  vec3 d = vec3(cos(lat) * sin(lon), sin(lat), -cos(lat) * cos(lon));
  gl_FragColor = textureCube(cube, d);
}`;

/**
 * 3D plan depth prepass: writes depth and a tiny base weight. Surfaces no render can see keep this colour:
 * a clean white "architectural model" finish, lit by the way each face points, rather than flat grey.
 */
export const FS_DOLL_PRE = String.raw`
precision highp float;
uniform float cutY;
uniform vec3 poche;
varying vec3 vW;
varying vec3 vN;
void main(){
  if (vW.y > cutY) discard;
  vec3 n = normalize(vN);
  if (!gl_FrontFacing) n = -n;
  vec3 L = normalize(vec3(0.35, 0.85, 0.4));
  float light = 0.68 + 0.26 * max(dot(n, L), 0.0) + 0.06 * max(n.y, 0.0);
  gl_FragColor = vec4(poche * light * 2e-6, 2e-6);
}`;

/**
 * 3D plan accumulation pass, four renders at a time, blended additively into a half-float target.
 * A surface takes its colour from the nearest renders that can see its front face.
 */
export const FS_DOLL = String.raw`
precision highp float;
${SLOT_UNIFORMS}
uniform float pOn[4];
uniform float cutY;
uniform float soft;
varying vec3 vW;
varying vec3 vN;
${EQ}
#define DVIS(D, dir, dist) (1.0 - smoothstep(textureCube(D, dir).r + 0.03 + dist * 0.012, textureCube(D, dir).r + 0.10 + dist * 0.03, dist))
#define DSLOT(I, T, D) if (pOn[I] > 0.5) { \
  vec3 dv = vW - pPos[I]; \
  float dist = max(length(dv), 1e-3); \
  vec3 dn = dv / dist; \
  float vis; \
  if (soft > 0.5) { \
    /* four taps around the direction, about a texel apart: smooth edges instead of stair steps */ \
    vec3 t1 = normalize(cross(dn, abs(dn.y) < 0.9 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0))); \
    vec3 t2 = cross(dn, t1); \
    float o = 0.0075; \
    vis = 0.25 * (DVIS(D, normalize(dn + t1 * o), dist) + DVIS(D, normalize(dn - t1 * o), dist) \
                + DVIS(D, normalize(dn + t2 * o), dist) + DVIS(D, normalize(dn - t2 * o), dist)); \
  } else vis = DVIS(D, dn, dist); \
  float nd = dot(n, dn); \
  if (nd > 0.02) vis = 0.0; \
  vec3 c = eqT(T, eq(dn, pYaw[I])).rgb; \
  if (vis > 0.001) { \
    float facing = 0.25 + 0.75 * abs(nd); \
    float w = vis * facing / (dist * dist * dist * dist + 0.002); \
    if (dbg > 0.5) c = mix(c, pCol[I], 0.55); \
    acc += c * w; tw += w; \
  } \
}
void main(){
  if (vW.y > cutY) discard;
  vec3 n = normalize(vN);
  vec3 acc = vec3(0.0); float tw = 0.0;
  DSLOT(0, tex0, dep0)
  DSLOT(1, tex1, dep1)
  DSLOT(2, tex2, dep2)
  DSLOT(3, tex3, dep3)
  gl_FragColor = vec4(acc, tw);
}`;

/** 3D plan composite: normalise the accumulated colour and restore depth for overlays. */
export const FS_DOLL_COMPOSITE = String.raw`
precision highp float;
uniform sampler2D acc;
uniform sampler2D dep;
uniform vec3 bgTop;
uniform vec3 bgBot;
varying vec2 vUv;
void main(){
  vec4 a = texture2D(acc, vUv);
  if (a.a <= 0.0) { gl_FragColor = vec4(mix(bgBot, bgTop, vUv.y), 1.0); gl_FragDepthEXT = 1.0; return; }
  gl_FragColor = vec4(a.rgb / a.a, 1.0);
  gl_FragDepthEXT = texture2D(dep, vUv).r;
}`;

/** Normals and log distance from a pano, for heading detection. */
export const FS_FEATURES = String.raw`
precision highp float;
uniform vec3 center;
varying vec3 vW;
varying vec3 vN;
void main(){
  float d = length(vW - center);
  gl_FragColor = vec4(abs(normalize(vN)), clamp(log(d + 0.5) / 4.0, 0.0, 1.0));
}`;

/**
 * Depth maps, one render at a time: the render's own depth mesh, coloured only by that render (so it can't
 * pick up another render's furniture). Alpha is how much to trust the pixel: low where the surface is seen
 * edge-on by the render, or from a very different angle than the viewer's.
 */
export const FS_LAYER = String.raw`
precision highp float;
uniform sampler2D tex;
uniform sampler2D eqd;
uniform vec3 pPos;
uniform float pYaw;
uniform float flipH;
uniform vec3 camPos;
uniform sampler2D cmpTex;
uniform float cmpOn;
uniform float cmpX;
varying vec3 vW;
varying vec3 vN;
${EQ}
void main(){
  vec3 dv = vW - pPos;
  float dist = max(length(dv), 1e-4);
  vec3 dn = dv / dist;
  vec2 suv = eq(dn, pYaw);
  /* the mesh reaches a little past each object's edge: trim it where the render saw something further away */
  if (dist < texture2D(eqd, suv).r - 0.04 - dist * 0.02) discard;
  vec3 c = (cmpOn > 0.5 && gl_FragCoord.x > cmpX) ? eqT(cmpTex, suv).rgb : eqT(tex, suv).rgb;
  vec3 n = normalize(cross(dFdx(vW), dFdy(vW)));
  float facing = abs(dot(n, dn));
  float ang = acos(clamp(dot(normalize(vW - camPos), dn), -1.0, 1.0));
  float conf = (0.05 + smoothstep(0.06, 0.35, facing)) / (ang + 0.05);
  gl_FragColor = vec4(c, conf);
}`;

/**
 * Depth maps, combined: per pixel, renders blend by their weight and trust, and a surface in front hides
 * the ones behind it in step with how much its render counts, so renders and their furniture fade in
 * and out as you walk instead of popping. Where no render saw anything (behind a stool,
 * from where you stand), the gap takes the colour of the background around it rather than the stool.
 * Nothing at all: discard, and the proxy view drawn underneath shows.
 */
export const FS_LAYER_COMPOSITE = String.raw`
precision highp float;
uniform sampler2D c0; uniform sampler2D c1; uniform sampler2D c2;
uniform sampler2D z0; uniform sampler2D z1; uniform sampler2D z2;
uniform float w[3];
uniform float n;
uniform float near;
uniform float far;
uniform vec2 px;
/* how much of this to show over the plain render underneath: none when standing exactly on a render */
uniform float mixA;
varying vec2 vUv;
float lin(float d){ return near * far / (far - d * (far - near)); }
vec4 lay(sampler2D c, sampler2D z, vec2 uv, out float zl){
  vec4 a = texture2D(c, uv);
  zl = a.a > 0.0 ? lin(texture2D(z, uv).r) : 1e9;
  return a;
}
/* how much a surface at zj in front hides one at zi: nothing while they agree (within ~4%), fully once well */
/* behind; and only as much as the render in front counts, so a render fading in also fades its objects in */
float occ(float zi, float zj, float sj){
  /* far away (the garden past the doors) renders from different spots disagree more, and it matters less */
  float tol = zj * (0.04 + 0.12 * smoothstep(5.0, 12.0, zj)) + 0.04;
  return smoothstep(1.0, 2.5, (zi - zj) / tol) * sj;
}
vec4 resolve(vec2 uv, out float zf, out float dz){
  float za, zb = 1e9, zc = 1e9;
  vec4 A = lay(c0, z0, uv, za);
  vec4 B = vec4(0.0), C = vec4(0.0);
  if (n > 1.5) B = lay(c1, z1, uv, zb);
  if (n > 2.5) C = lay(c2, z2, uv, zc);
  zf = min(za, min(zb, zc));
  dz = 1.0;
  if (zf > 1e8) return vec4(0.0);
  float sa = smoothstep(0.0, 0.3, w[0]), sb = smoothstep(0.0, 0.3, w[1]), sc = smoothstep(0.0, 0.3, w[2]);
  /* a render with no weight still fills a gap no one else covers (the tiny floor), and nothing else */
  float ka = (w[0] + 1e-4) * A.a * (1.0 - occ(za, zb, sb)) * (1.0 - occ(za, zc, sc));
  float kb = (w[1] + 1e-4) * B.a * (1.0 - occ(zb, za, sa)) * (1.0 - occ(zb, zc, sc));
  float kc = (w[2] + 1e-4) * C.a * (1.0 - occ(zc, za, sa)) * (1.0 - occ(zc, zb, sb));
  float t = ka + kb + kc;
  if (t <= 1e-7) return vec4(0.0);
  /* depth for the markers: the surface that counts most */
  if (ka >= kb && ka >= kc) dz = texture2D(z0, uv).r; else if (kb >= kc) dz = texture2D(z1, uv).r; else dz = texture2D(z2, uv).r;
  return vec4((A.rgb * ka + B.rgb * kb + C.rgb * kc) / t, 1.0);
}
void main(){
  float zf, dz;
  vec4 col = resolve(vUv, zf, dz);
  if (col.a > 0.0) { gl_FragColor = vec4(col.rgb, mixA); gl_FragDepthEXT = dz; return; }
  /* a gap no render saw (the floor behind a chair as you step sideways): look out from it in eight */
  /* directions to the first surface each way. The nearest of those is what's in front (the chair); of the */
  /* ones well behind it, the closest on screen is the background next to the gap (the floor), not the */
  /* far wall above it. The gap takes that colour. */
  float zs[8]; vec3 cs[8]; float ds[8]; float rs[8];
  float zmin = 1e9;
  for (int a = 0; a < 8; a++) {
    float t = float(a) * 0.785398;
    vec2 dir = vec2(cos(t), sin(t)) * px;
    zs[a] = -1.0; cs[a] = vec3(0.0); ds[a] = 1.0; rs[a] = 1e9;
    for (int k = 0; k < 6; k++) {
      float r = 2.0 * pow(2.0, float(k));
      float z, d;
      vec4 c = resolve(vUv + dir * r, z, d);
      if (c.a > 0.0) { zs[a] = z; cs[a] = c.rgb; ds[a] = d; rs[a] = r; break; }
    }
    if (zs[a] > 0.0) zmin = min(zmin, zs[a]);
  }
  float rbest = 1e9, bz = -1.0, bd = 1.0;
  for (int a = 0; a < 8; a++) if (zs[a] > zmin * 1.08 + 0.05 && rs[a] < rbest) rbest = rs[a];
  /* nothing clearly behind: everything around is one surface, so just carry it on */
  bool any = rbest < 1e8;
  vec3 best = vec3(0.0); float bw = 0.0;
  for (int a = 0; a < 8; a++) {
    if (zs[a] <= 0.0) continue;
    bool use = any ? (zs[a] > zmin * 1.08 + 0.05 && rs[a] <= rbest * 2.0) : true;
    if (use) { best += cs[a]; bw += 1.0; if (zs[a] > bz) { bz = zs[a]; bd = ds[a]; } }
  }
  if (bw > 0.0) best /= bw;
  if (bz < 0.0) discard;
  gl_FragColor = vec4(best, mixA);
  gl_FragDepthEXT = bd;
}`;
